import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Sparkles, Users, Wallet, FolderOpen, Shield, FileText, Copy, Zap, MessageSquare, Database, Scale, Vote, ShieldCheck } from 'lucide-react';
import {
  Button,
  Card,
  Badge
} from '../components/ui';
import { cn } from '../lib/utils';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { NodeCreationWizard } from '../components/shell/NodeCreationWizard';
import { BoardPitchModal } from '../components/shell/BoardPitchModal';

import { SEO } from '../components/SEO';

export const PricingPage = () => {
  const brandName = 'OrgSets';
  const navigate = useNavigate();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isBoardPitchModalOpen, setIsBoardPitchModalOpen] = useState(false);

  const s = {
    wrapper: 'bg-white dark:bg-black text-black dark:text-white',
    section: '',
    card: 'bg-white dark:bg-black text-black dark:text-white transition-all hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black group',
    featuredCard: 'bg-brand-primary text-white dark:bg-brand-primary dark:text-white transition-all hover:bg-black dark:hover:bg-white dark:hover:text-black group z-10',
    title: 'font-black uppercase tracking-tighter',
    price: 'font-black',
    desc: 'font-bold opacity-80'
  };

  const renderPlan = (plan: any, i: number) => {
    const currentPhase = parseInt(import.meta.env.VITE_APP_PHASE || '4');
    const isComingSoon = plan.phase > currentPhase;
    const isFeatured = plan.featured && !isComingSoon;

    return (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: i * 0.1 }}
        className="z-10 mt-0 flex"
      >
        <div className={cn(
          "w-full flex flex-col transition-all duration-500 relative",
          "border-b-[4px] border-r-[4px] border-black dark:border-white rounded-none shadow-none",
          isFeatured ? cn(s.featuredCard, "p-8") : cn(s.card, "p-8"),
          isComingSoon && "opacity-60 grayscale-[0.5]"
        )}>
          {isFeatured && (
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-30">
              <div className={cn("rounded-full", s.section)}>
                <span className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[10px] font-black tracking-widest uppercase shadow-md whitespace-nowrap transition-all duration-300 bg-brand-secondary text-black border-[4px] border-black rounded-none shadow-[4px_4px_0px_#000]">
                  <Sparkles size={11} className="shrink-0 animate-pulse text-current" />
                  <span>Most Popular</span>
                </span>
              </div>
            </div>
          )}

          <div className="flex justify-between items-start mb-2">
            <h3 className={cn("text-2xl font-bold font-display tracking-tight", s.title)}>{plan.name}</h3>
            {isComingSoon && (
              <Badge variant="amber" className="text-[10px] h-5 py-0 font-black tracking-widest uppercase bg-amber-500/10 text-amber-600 border-none">SOON</Badge>
            )}
          </div>
          <p className="text-[10px] font-black uppercase tracking-widest opacity-40 dark:opacity-60 mb-6 min-h-[28px]">{plan.desc}</p>

          <div className="mb-6 relative">
            <div className="flex items-baseline gap-1">
              <span className={cn("text-5xl font-black font-display", s.price)}>${plan.price}</span>
              <span className="text-gray-400 dark:text-gray-300 text-xs font-bold uppercase tracking-wider">/mo</span>
            </div>
            {plan.name === 'Momentum' && (
              <div className="absolute -bottom-5 left-0 text-[10px] font-bold flex items-center gap-1.5 px-2 py-0.5 rounded-full whitespace-nowrap bg-black text-white dark:bg-white dark:text-black border-[2px] border-black dark:border-white">
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-white dark:bg-black"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white dark:bg-black"></span>
                </span>
                Unlocks Real-time Chat
              </div>
            )}
          </div>

          <div className="h-px w-full bg-gray-100 dark:bg-white/10 mb-6 mt-2" />

          <ul className="space-y-4 mb-10 flex-grow">
            {plan.features.map((f: string, j: number) => {
              const isKeyFeature = f.includes('Chat') || f.includes('Newsletter') || f.includes('2GB');
              const isInherited = f.startsWith('+');
              return (
                <li key={j} className={cn("flex items-start gap-3 text-sm leading-tight", isInherited ? "opacity-60 italic" : "font-medium")}>
                  {isInherited ? (
                    <ArrowRight size={14} className="shrink-0 mt-0.5 text-gray-400" />
                  ) : (
                    <CheckCircle2
                      size={16}
                      className={cn(
                        "shrink-0 mt-0.5",
                        isFeatured ? "text-inherit" : "text-brand-primary group-hover:text-inherit"
                      )}
                    />
                  )}
                  <span className={cn(
                    isInherited ? s.desc : "font-extrabold"
                  )}>
                    {f}
                  </span>
                </li>
              );
            })}
          </ul>

          <Button
            variant={isFeatured ? 'primary' : 'secondary'}
            disabled={isComingSoon}
            onClick={() => setIsWizardOpen(true)}
            className={cn(
              "w-full h-12 text-sm font-black uppercase tracking-widest transition-all duration-300 relative overflow-hidden mt-auto",
              "border-[4px] border-black group-hover:bg-white group-hover:text-black dark:border-white dark:group-hover:bg-black dark:group-hover:text-white"
            )}
          >
            {isComingSoon ? 'Coming Soon' : (plan.price === '0' ? 'Start Free Org' : 'Upgrade Now')}
          </Button>
        </div>
      </motion.div>
    );
  };

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${brandName} Subscription Plans`,
    "description": `Flexible pricing plans for community groups, growing chapters, and enterprise-grade networks.`,
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "0",
      "highPrice": "199",
      "offers": [
        { "@type": "Offer", "name": "Local", "price": "0", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Foundation", "price": "29", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Momentum", "price": "79", "priceCurrency": "USD" },
        { "@type": "Offer", "name": "Institutional", "price": "199", "priceCurrency": "USD" }
      ]
    }
  };

  return (
    <div className={cn("min-h-screen font-sans", s.wrapper)}>
      <SEO
        title="Pricing | Association & Chapter Management Plans | OrgSets"
        description="View our pricing plans. Start free for local chapters and seamlessly upgrade for advanced ledgers, automated workflows, and multi-tier network governance."
        url="https://orgsets.com/plans"
        schema={pricingSchema}
      />
      <PublicHeader />

      <main>
        <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[60vh] bg-black text-white dark:bg-white dark:text-black mb-24">
              <div className="p-8 pt-28 md:p-16 md:pt-28 lg:p-12 lg:pt-28 xl:p-24 xl:pt-28 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
                <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words text-white dark:text-black">
                  Scale Your <br/> Impact. Not <br/> Overhead.
                </h1>
                <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
                  All core modules included. Start for free, and unlock powerful reporting and league tools as you grow.
                  <span className="text-sm font-bold opacity-80 mt-4 block">Setup in 60 seconds. No credit card required.</span>
                </p>
              </div>
              <div className="p-8 md:p-16 flex flex-col justify-center items-center relative overflow-hidden bg-white text-black dark:bg-black dark:text-white">
                <div className="text-[100px] md:text-[150px] font-black leading-none tracking-tighter mb-4">$0</div>
                <div className="text-2xl md:text-4xl font-bold mb-12 text-pink-500 uppercase tracking-widest text-center">To Launch an Org</div>
                <div className="border-[4px] border-black dark:border-white px-8 py-4 font-black uppercase tracking-widest text-xl bg-brand-secondary text-black">Zero Base Fees</div>
              </div>
            </div>

          <div className="max-w-7xl mx-auto relative z-10">
            {/* All Tiers */}
            <div className="mb-20">
              <div className={cn(
                "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 items-stretch border-t-[4px] border-l-[4px] border-black dark:border-white"
              )}>
                {[
                  {
                    name: 'Local',
                    price: '0',
                    features: ['Basic Directory & Ledger', 'Email Newsletters', 'Flat $0.50 Platform Fee/tx', '100MB Vault Storage', '50 Member Roster', 'Single Chapter'],
                    phase: 1,
                    desc: 'Perfect for small clubs'
                  },
                  {
                    name: 'Foundation',
                    price: '29',
                    features: ['+ All Local features', 'Flat $0.40 Platform Fee/tx', '1GB Vault Storage', '250 Member Roster', 'Custom Branding & Terminology'],
                    phase: 1,
                    desc: 'Growing organizations'
                  },
                  {
                    name: 'Momentum',
                    price: '79',
                    features: ['+ All Foundation features', 'Real-time WebSocket Chat', 'Flat $0.30 Platform Fee/tx', '5GB Vault Storage', '1,000 Member Roster', 'Chapter + Sub-teams', 'Bundled SMS Power-up (200/mo)'],
                    phase: 2,
                    desc: 'Multi-chapter ops',
                    featured: true
                  },
                  {
                    name: 'Institutional',
                    price: '199',
                    features: ['+ All Momentum features', 'Cascading Newsletters', 'Flat $0.20 Platform Fee/tx', '25GB Vault Storage', '5,000 Member Roster', 'HQ, Regions & Local Chapters', '1,000 SMS messages/mo', 'Included Power-ups (Governance & Elections)'],
                    phase: 2,
                    desc: 'State & District level'
                  }
                ].map((plan, i) => renderPlan(plan, i))}
              </div>
            </div>

            {/* Enterprise / National Tier Banner */}
            <div className="max-w-5xl mx-auto mb-20">
              <div className="relative overflow-hidden border-2 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8 bg-brand-secondary text-black border-[8px] border-black dark:border-white shadow-[16px_16px_0px_#000] dark:shadow-[16px_16px_0px_#fff] rounded-none">
                {/* Decorative background */}
                <div className="absolute top-0 right-0 -mt-16 -mr-16 text-brand-primary/5">
                  <svg width="200" height="200" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L2 22h20L12 2z" /></svg>
                </div>

                <div className="flex-1 relative z-10">
                  <Badge variant="blue" className="mb-4 border-none bg-black text-white px-4">ENTERPRISE</Badge>
                  <h3 className="text-3xl font-black font-display mb-2 text-black">National Level</h3>
                  <p className="text-lg mb-6 text-black/80 font-bold">For national headquarters and global networks requiring complex hierarchical routing, custom compliance, and dedicated support.</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {['Unlimited Hierarchical Depth', 'All Power-ups Across Network', 'Multi-Tier Grant Routing', 'Cascading Waivers', 'Custom API & SSO', 'White-label Mobile App'].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm font-medium">
                        <CheckCircle2 size={16} className="text-black" />
                        <span className="text-black font-bold">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col items-center md:items-end relative z-10 shrink-0">
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-5xl font-black font-display text-black">$499</span>
                    <span className="text-sm font-bold uppercase tracking-wider text-black/60">/mo</span>
                  </div>
                  <Button variant="primary" size="lg" className="w-full md:w-auto shadow-lg shadow-brand-primary/25 hover:shadow-xl hover:shadow-brand-primary/45 hover:scale-[1.02] transition-all">
                    Build Enterprise Network
                  </Button>
                </div>
              </div>
            </div>

            {/* Board & Treasurer Approval Pack Banner */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="p-8 flex flex-col md:flex-row items-center justify-between gap-8 text-left border transition-all bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[12px_12px_0px_#0044FF] dark:shadow-[12px_12px_0px_#FFD600] rounded-none">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={18} className="text-[#0044FF] dark:text-[#FFD600]" />
                    <span className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600]">
                      Board & Treasurer Approval Pack
                    </span>
                  </div>
                  <h4 className={cn("text-2xl font-black font-display uppercase tracking-tight mb-2", s.title)}>
                    Need approval from your President or Treasurer?
                  </h4>
                  <p className={cn("text-sm max-w-lg font-medium", s.desc)}>
                    Don't start from scratch. Download our printable 1-page executive memo or copy pre-written pitch blurbs addressing software cost reduction, Stripe banking safeguards, and data privacy.
                  </p>
                </div>
                <div className="shrink-0 w-full md:w-auto flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="primary"
                    onClick={() => navigate('/board-memo')}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-none bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider px-6 py-4 hover:bg-[#0044FF] hover:text-white transition-colors border-[3px] border-black dark:border-white"
                  >
                    <FileText size={16} />
                    View 1-Page Memo
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setIsBoardPitchModalOpen(true)}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-none border-[3px] border-black dark:border-white text-black dark:text-white font-black uppercase text-xs tracking-wider px-6 py-4 hover:bg-[#FFD600] hover:text-black transition-colors"
                  >
                    <Copy size={16} />
                    Quick Pitch Blurbs
                  </Button>
                </div>
              </div>
            </div>

            {/* Pitch to HQ Banner */}
            <div className="max-w-4xl mx-auto mb-16">
              <div className="p-8 flex flex-col md:flex-row items-center justify-between gap-8 text-left border transition-all hover:shadow-lg bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[12px_12px_0px_#000] dark:shadow-[12px_12px_0px_#fff] rounded-none">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={18} className="text-brand-primary" />
                    <span className="text-sm font-bold uppercase tracking-widest text-brand-primary">Grassroots Growth</span>
                  </div>
                  <h4 className={cn("text-2xl font-black font-display mb-2", s.title)}>Part of a larger network?</h4>
                  <p className={cn("text-base max-w-lg", s.desc)}>
                    Are you a local chapter or club? Generate a custom proposal to pitch OrgSets to your National or Regional Headquarters and unlock platform-wide funding and compliance tools.
                  </p>
                </div>
                <div className="shrink-0 w-full md:w-auto">
                  <Button variant="secondary" onClick={() => navigate('/tools/hq-pitch-generator')} className="w-full md:w-auto flex items-center justify-center gap-2 group whitespace-nowrap border-2 border-brand-primary/20 hover:border-brand-primary/50 bg-white/50 dark:bg-black/50 backdrop-blur-md">
                    Generate HQ Pitch <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Treasurer & Board Transparency FAQ */}
            <div className="max-w-5xl mx-auto mb-16 border-[4px] border-black dark:border-white p-8 md:p-12 bg-white dark:bg-black shadow-[12px_12px_0px_#000] dark:shadow-[12px_12px_0px_#fff] rounded-none">
              <div className="flex items-center gap-2 mb-3">
                <Shield size={20} className="text-brand-primary" />
                <span className="text-xs font-mono font-bold uppercase tracking-widest text-brand-primary">
                  Board & Treasurer Transparency
                </span>
              </div>
              <h3 className="text-3xl md:text-4xl font-black font-display uppercase tracking-tight mb-8">
                How Our Business Model Works
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-3">
                  <h4 className="text-lg font-black uppercase tracking-tight">How does Free to Start work?</h4>
                  <p className="text-sm font-bold opacity-80 leading-relaxed">
                    Local chapters start completely free for up to 50 members. We earn a flat $0.50 platform fee per transaction when members pay dues online via Stripe. When your organization grows past 50 members or needs multi-tier parent networks, you upgrade to our paid plans.
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="text-lg font-black uppercase tracking-tight">Any hidden fees or lock-in?</h4>
                  <p className="text-sm font-bold opacity-80 leading-relaxed">
                    Zero. No setup fees, no monthly minimums, and no credit card required to start. You can export your full member rosters and financial ledger history anytime with a single click.
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="text-lg font-black uppercase tracking-tight">Do you sell member data?</h4>
                  <p className="text-sm font-bold opacity-80 leading-relaxed">
                    Never. Your organization's members, contact information, and financial records belong 100% to your organization. We never show ads, sell contact lists, or monetize your community data.
                  </p>
                </div>
              </div>
            </div>

            {/* Modular Power-ups & Add-ons Section */}
            <div className="max-w-5xl mx-auto mb-20">
              <div className="border-[4px] border-black dark:border-white bg-white dark:bg-black p-8 md:p-12 shadow-[12px_12px_0px_#0044FF] dark:shadow-[12px_12px_0px_#FFD600] rounded-none">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Zap size={18} className="text-[#0044FF] dark:text-[#FFD600]" />
                      <span className="text-xs font-mono font-black uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600]">
                        Modular Add-Ons
                      </span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-black font-display uppercase tracking-tight">
                      Modular Power-Ups & À La Carte Pricing
                    </h3>
                  </div>
                  <p className="text-sm font-bold opacity-80 max-w-md">
                    Need a specific capability without jumping to a higher tier? Add modular power-ups to any plan, or get them bundled automatically.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse border-[2px] border-black dark:border-white">
                    <thead>
                      <tr className="bg-black text-white dark:bg-white dark:text-black font-mono text-xs uppercase tracking-widest">
                        <th className="p-4 border-[2px] border-black dark:border-white">Power-Up</th>
                        <th className="p-4 border-[2px] border-black dark:border-white">Capability</th>
                        <th className="p-4 border-[2px] border-black dark:border-white">À La Carte Price</th>
                        <th className="p-4 border-[2px] border-black dark:border-white">Bundled Free In</th>
                      </tr>
                    </thead>
                    <tbody className="font-sans text-sm divide-y-2 divide-black dark:divide-white">
                      {[
                        {
                          name: "SMS Mega-Bundle",
                          icon: <MessageSquare size={16} className="text-[#0044FF]" />,
                          desc: "500 high-priority SMS notifications for urgent Pulse broadcasts & weather alerts.",
                          price: "$19 / pack",
                          included: "Momentum (200/mo) • Institutional (1,000/mo)"
                        },
                        {
                          name: "Vault Storage Expansion",
                          icon: <Database size={16} className="text-rose-500" />,
                          desc: "Adds +25GB encrypted cloud storage for blueprints, bylaws, audit archives, and high-res files.",
                          price: "$5 / mo",
                          included: "25GB in Institutional • Unlimited in Enterprise"
                        },
                        {
                          name: "Election Guard",
                          icon: <Vote size={16} className="text-amber-500" />,
                          desc: "Secure, anonymized annual board elections with cryptographic verification and audit logs.",
                          price: "$10 / election",
                          included: "Included in Institutional & Enterprise"
                        },
                        {
                          name: "Legal & Bylaws Blueprint",
                          icon: <Scale size={16} className="text-purple-500" />,
                          desc: "Full access to legally vetted bylaws, grievance templates, and conflict resolution workflows.",
                          price: "$15 / mo",
                          included: "Included in Institutional & Enterprise"
                        },
                        {
                          name: "Passport Vetting Pack",
                          icon: <ShieldCheck size={16} className="text-emerald-500" />,
                          desc: "Pre-paid block of 10 identity verification and background checks for leaders & volunteers.",
                          price: "$49 / pack",
                          included: "Custom quotas on Enterprise"
                        }
                      ].map((powerup, idx) => (
                        <tr 
                          key={powerup.name}
                          className={cn(
                            "border-[2px] border-black dark:border-white font-medium transition-colors hover:bg-slate-50 dark:hover:bg-slate-900",
                            idx % 2 === 1 ? "bg-slate-50/50 dark:bg-white/5" : "bg-transparent"
                          )}
                        >
                          <td className="p-4 border-[2px] border-black dark:border-white">
                            <div className="flex items-center gap-2.5 font-bold">
                              <span className="shrink-0">{powerup.icon}</span>
                              <span className="font-display uppercase tracking-tight text-sm font-black">{powerup.name}</span>
                            </div>
                          </td>
                          <td className="p-4 border-[2px] border-black dark:border-white text-xs font-bold opacity-80 leading-relaxed">
                            {powerup.desc}
                          </td>
                          <td className="p-4 border-[2px] border-black dark:border-white whitespace-nowrap font-mono font-black text-sm text-[#0044FF] dark:text-[#FFD600]">
                            {powerup.price}
                          </td>
                          <td className="p-4 border-[2px] border-black dark:border-white whitespace-nowrap text-xs font-mono font-bold">
                            <span className="bg-[#FFD600] text-black px-2 py-1 border border-black text-[10px] font-black uppercase tracking-wider inline-block">
                              {powerup.included}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 pt-6 border-t-2 border-black dark:border-white flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono font-bold">
                  <div className="flex items-center gap-2">
                    <span className="text-[#0044FF] dark:text-[#FFD600]">★</span>
                    <span><strong>Enterprise Network Advantage:</strong> $499/mo Enterprise plans include all Power-ups across every child node in the hierarchy.</span>
                  </div>
                  <Button 
                    variant="secondary"
                    onClick={() => navigate('/powerups')}
                    className="shrink-0 text-[10px] font-black uppercase tracking-widest px-4 py-2 border-[2px] border-black dark:border-white rounded-none hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors"
                  >
                    Manage Org Power-ups →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />

      <NodeCreationWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} />
      <BoardPitchModal isOpen={isBoardPitchModalOpen} onClose={() => setIsBoardPitchModalOpen(false)} />
    </div>
  );
};
